import React, { useEffect, useState } from "react";
import {
  Product,
  productService,
} from "../../../services/product/product.service";
import {
  ProductCategory,
  productCategoryService,
} from "../../../services/product/product-category.service";
import { Search } from "lucide-react";

interface ProductPageProps {
  showSearchAndFilter?: boolean;
  limit?: number;
}

const ProductPage: React.FC<ProductPageProps> = ({
  showSearchAndFilter = true,
  limit,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<ProductCategory[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch both products and categories in parallel
        const [productList, categoryList] = await Promise.all([
          productService.getAllProductsWithCategories(),
          productCategoryService.getProductCategories(),
        ]);

        setProducts(productList);
        setCategories(categoryList);

        // Apply limit if specified
        const limitedProducts = limit
          ? productList.slice(0, limit)
          : productList;
        setFilteredProducts(limitedProducts);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [limit]);

  const filterProducts = () => {
    let filtered = products;

    // Category tab filter
    if (activeTab !== "all") {
      filtered = filtered.filter(
        (product) => product.category_name === activeTab
      );
    }
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.latin_name
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (product.category_name?.toLowerCase() || "").includes(
            searchTerm.toLowerCase()
          )
      );
    }

    // Apply default sorting by name
    filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));

    // Apply limit if specified
    filtered = limit ? filtered.slice(0, limit) : filtered;
    setFilteredProducts(filtered);
  };

  // Trigger filtering whenever relevant state changes
  useEffect(() => {
    filterProducts();
  }, [searchTerm, products, activeTab, limit]);

  // Handle tab click
  const handleTabClick = (categoryName: string) => {
    setActiveTab(categoryName);
  };

  const handleDetailClick = (productName: string) => {
    const encodedName = encodeURIComponent(productName);
    window.location.href = `/layanan/produk-layanan/${encodedName}`;
  };

  const handleViewAllClick = () => {
    window.location.href = "/layanan/produk-layanan";
  };

  // Hitung jumlah produk per kategori untuk badge
  const getProductCountByCategory = (categoryName: string) => {
    if (categoryName === "all") return products.length;
    return products.filter((p) => p.category_name === categoryName).length;
  };

  return (
    <div className="w-full">
      {/* Sticky Header Container */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        {/* Main Content Area */}
        <div className="p-4">
          {/* Search Input */}
          {showSearchAndFilter && (
            <div className="relative mb-4">
              <Search className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
              <input
                type="text"
                placeholder="Cari produk..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-3 pl-10 pr-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Category Tabs */}
          {categories.length > 0 && (
            <div className="mb-2">
              <div className="overflow-x-auto border-b border-gray-200">
                <nav className="flex gap-1 -mb-px flex-nowrap min-w-max">
                  {/* Tab "Semua Produk" */}
                  <button
                    onClick={() => handleTabClick("all")}
                    className={`
                      py-3 px-5 text-sm font-medium
                      border-b-2 transition-all duration-200 whitespace-nowrap
                      ${
                        activeTab === "all"
                          ? "border-blue-600 text-blue-600 bg-blue-50"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }
                    `}
                  >
                    Semua Produk
                    <span className="ml-2 bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                      {getProductCountByCategory("all")}
                    </span>
                  </button>

                  {/* Tabs untuk setiap kategori */}
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleTabClick(category.name)}
                      className={`
                        py-3 px-5 text-sm font-medium
                        border-b-2 transition-all duration-200 whitespace-nowrap
                        ${
                          activeTab === category.name
                            ? "border-blue-600 text-blue-600 bg-blue-50"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }
                      `}
                    >
                      {category.name}
                      <span className="ml-2 bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                        {getProductCountByCategory(category.name)}
                      </span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product Grid Container */}
      <div className="p-4">
        {/* Product Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-b-2 border-blue-500 rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Product Count Info */}
            <div className="mb-4 text-sm text-gray-600">
              Menampilkan {filteredProducts.length} dari {products.length} produk
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className="overflow-hidden transition duration-300 transform bg-white rounded-lg shadow-lg hover:scale-105 hover:shadow-xl"
                >
                  <div className="relative group">
                    <div className="relative h-64 bg-gray-100">
                      <img
                        src={
                          Array.isArray(product.images)
                            ? `${process.env.REACT_APP_API_URL}${product.images[0]?.image || ''}`
                            : `${process.env.REACT_APP_API_URL}${product.images || ''}`
                        }
                        alt={product.name}
                        className="absolute inset-0 object-contain w-full h-full p-4"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder-image.jpg';
                        }}
                      />
                      <div className="absolute inset-0 transition-opacity duration-300 bg-black opacity-0 group-hover:opacity-20"></div>
                    </div>
                  </div>
                  <div className="p-6">
                    {product.category_name && (
                      <div className="inline-block px-2 py-1 mb-2 text-xs font-bold text-blue-700 bg-blue-100 rounded-full">
                        {product.category_name}
                      </div>
                    )}
                    <h3 className="mb-1 text-lg font-semibold text-gray-900 line-clamp-2">
                      {product.name}
                    </h3>
                    <p className="mb-3 text-sm italic text-gray-600 line-clamp-1">
                      {product.latin_name}
                    </p>
                    <span className="block mb-4 text-xl font-bold text-gray-900">
                      Rp {product.price.toLocaleString('id-ID')}
                    </span>
                    <button
                      onClick={() => handleDetailClick(product.name)}
                      className="w-full px-4 py-2 font-semibold text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                      Detail
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {limit && products.length > limit && (
              <div className="flex justify-center mt-12 mb-8">
                <button
                  onClick={handleViewAllClick}
                  className="px-8 py-3 font-semibold text-white transition-colors bg-blue-600 rounded-lg shadow-md hover:bg-blue-700 hover:shadow-lg"
                >
                  Lihat Semua Produk
                </button>
              </div>
            )}
          </>
        )}

        {!loading && filteredProducts.length === 0 && (
          <div className="py-12 text-center">
            <div className="mb-4 text-gray-400">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              Tidak ada produk ditemukan
            </h3>
            <p className="text-gray-600">
              Coba sesuaikan pencarian Anda untuk menemukan produk yang Anda cari.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductPage;